// Dynamic Pricing Intelligence Engine
// Tracks price changes across retailers and provides alerts

export interface PricePoint {
  retailer: string
  price: number
  currency: string
  timestamp: Date
  url: string
  inStock: boolean
  shipping?: number
}

export interface PriceHistory {
  productId: string
  currentPrice: number
  lowestPrice: number
  highestPrice: number
  averagePrice: number
  pricePoints: PricePoint[]
  lastUpdated: Date
  trend: 'rising' | 'falling' | 'stable'
  changePercentage: number
}

export interface PriceAlert {
  id: string
  productId: string
  userId?: string
  targetPrice: number
  currentPrice: number
  alertType: 'drop_below' | 'drop_percentage' | 'back_in_stock'
  isActive: boolean
  createdAt: Date
  triggeredAt?: Date
}

export class PricingEngine {
  
  static async getPriceHistory(productId: string, days: number = 30): Promise<PriceHistory | null> {
    try {
      // In production, this would query a database with historical price data
      // For now, simulate realistic price history
      const mockPriceHistory = this.generateMockPriceHistory(productId, days)
      return mockPriceHistory
    } catch (error) {
      console.error('Failed to fetch price history:', error)
      return null
    }
  }
  
  static async getCurrentPrices(productId: string): Promise<PricePoint[]> {
    try {
      // Mock current prices across multiple retailers
      const mockPrices: PricePoint[] = [
        {
          retailer: 'Amazon',
          price: 198.94,
          currency: 'USD',
          timestamp: new Date(),
          url: `https://amazon.com/dp/product-${productId}`,
          inStock: true,
          shipping: 0
        },
        {
          retailer: 'Walmart', 
          price: 189.99,
          currency: 'USD',
          timestamp: new Date(),
          url: `https://walmart.com/product-${productId}`,
          inStock: true,
          shipping: 5.99
        },
        {
          retailer: 'Best Buy',
          price: 204.99,
          currency: 'USD', 
          timestamp: new Date(),
          url: `https://bestbuy.com/product-${productId}`,
          inStock: false,
          shipping: 0
        }
      ]
      
      return mockPrices
    } catch (error) {
      console.error('Failed to fetch current prices:', error)
      return []
    }
  }
  
  static async trackPriceChange(productId: string, oldPrice: number, newPrice: number, retailer: string) {
    const changePercentage = ((newPrice - oldPrice) / oldPrice) * 100
    const isSignificant = Math.abs(changePercentage) >= 5 // 5% threshold
    
    if (isSignificant) {
      // Store price change event
      if (typeof window !== 'undefined') {
        const priceChanges = JSON.parse(localStorage.getItem('mc_price_changes') || '[]')
        priceChanges.push({
          productId,
          retailer,
          oldPrice,
          newPrice,
          changePercentage: Math.round(changePercentage * 100) / 100,
          timestamp: Date.now()
        })
        
        // Keep only last 100 price changes
        if (priceChanges.length > 100) {
          priceChanges.splice(0, priceChanges.length - 100)
        }
        
        localStorage.setItem('mc_price_changes', JSON.stringify(priceChanges))
      }
      
      // Check if this triggers any price alerts
      await this.checkPriceAlerts(productId, newPrice, retailer)
    }
  }
  
  static async createPriceAlert(alert: Omit<PriceAlert, 'id' | 'createdAt'>): Promise<string> {
    const alertId = `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const newAlert: PriceAlert = {
      id: alertId,
      ...alert,
      createdAt: new Date()
    }
    
    if (typeof window !== 'undefined') {
      const alerts = JSON.parse(localStorage.getItem('mc_price_alerts') || '[]')
      alerts.push(newAlert)
      localStorage.setItem('mc_price_alerts', JSON.stringify(alerts))
    }
    
    return alertId
  }
  
  static async getPriceAlerts(userId?: string): Promise<PriceAlert[]> {
    if (typeof window === 'undefined') return []
    
    try {
      const alerts = JSON.parse(localStorage.getItem('mc_price_alerts') || '[]')
      return alerts.filter((alert: PriceAlert) => 
        alert.isActive && (userId ? alert.userId === userId : true)
      )
    } catch {
      return []
    }
  }
  
  private static async checkPriceAlerts(productId: string, newPrice: number, retailer: string) {
    const alerts = await this.getPriceAlerts()
    const productAlerts = alerts.filter(alert => alert.productId === productId)
    
    for (const alert of productAlerts) {
      let shouldTrigger = false
      
      switch (alert.alertType) {
        case 'drop_below':
          shouldTrigger = newPrice <= alert.targetPrice
          break
        case 'drop_percentage':
          const dropPercentage = ((alert.currentPrice - newPrice) / alert.currentPrice) * 100
          shouldTrigger = dropPercentage >= alert.targetPrice // targetPrice is percentage here
          break
        case 'back_in_stock':
          // Would need to check stock status, for now assume price change means back in stock
          shouldTrigger = true
          break
      }
      
      if (shouldTrigger) {
        await this.triggerPriceAlert(alert, newPrice, retailer)
      }
    }
  }
  
  private static async triggerPriceAlert(alert: PriceAlert, newPrice: number, retailer: string) {
    // Mark alert as triggered
    alert.triggeredAt = new Date()
    alert.isActive = false
    
    // Update in storage
    if (typeof window !== 'undefined') {
      const alerts = JSON.parse(localStorage.getItem('mc_price_alerts') || '[]')
      const index = alerts.findIndex((a: PriceAlert) => a.id === alert.id)
      if (index >= 0) {
        alerts[index] = alert
        localStorage.setItem('mc_price_alerts', JSON.stringify(alerts))
      }
    }
    
    // In production, would send email/push notification
    console.log(`Price alert triggered! Product ${alert.productId} is now $${newPrice} at ${retailer}`)
    
    // Show browser notification if permission granted
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Price Drop Alert! 🔥', {
        body: `Your watched product is now $${newPrice} at ${retailer}`,
        icon: '/icons/icon-192.png'
      })
    }
  }
  
  private static generateMockPriceHistory(productId: string, days: number): PriceHistory {
    const currentPrice = 198.94
    const pricePoints: PricePoint[] = []
    const basePrice = currentPrice
    
    // Generate price points for each day
    for (let i = days; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      
      // Simulate price fluctuation (+/- 10%)
      const variance = (Math.random() - 0.5) * 0.2 // -10% to +10%
      const price = Math.round((basePrice * (1 + variance)) * 100) / 100
      
      pricePoints.push({
        retailer: 'Amazon',
        price,
        currency: 'USD',
        timestamp: date,
        url: `https://amazon.com/dp/product-${productId}`,
        inStock: Math.random() > 0.1, // 90% chance in stock
        shipping: price > 150 ? 0 : 5.99
      })
    }
    
    // Calculate statistics
    const prices = pricePoints.map(p => p.price)
    const lowestPrice = Math.min(...prices)
    const highestPrice = Math.max(...prices)
    const averagePrice = Math.round((prices.reduce((sum, p) => sum + p, 0) / prices.length) * 100) / 100
    
    // Calculate trend
    const recentPrices = prices.slice(-7) // Last 7 days
    const oldPrices = prices.slice(-14, -7) // Previous 7 days
    const recentAvg = recentPrices.reduce((sum, p) => sum + p, 0) / recentPrices.length
    const oldAvg = oldPrices.reduce((sum, p) => sum + p, 0) / oldPrices.length
    
    let trend: 'rising' | 'falling' | 'stable' = 'stable'
    const changePercentage = ((recentAvg - oldAvg) / oldAvg) * 100
    
    if (changePercentage > 3) trend = 'rising'
    else if (changePercentage < -3) trend = 'falling'
    
    return {
      productId,
      currentPrice,
      lowestPrice,
      highestPrice,
      averagePrice,
      pricePoints,
      lastUpdated: new Date(),
      trend,
      changePercentage: Math.round(changePercentage * 100) / 100
    }
  }
  
  static getBestCurrentPrice(prices: PricePoint[]): PricePoint | null {
    const inStockPrices = prices.filter(p => p.inStock)
    if (inStockPrices.length === 0) return null
    
    // Find lowest total price (price + shipping)
    return inStockPrices.reduce((best, current) => {
      const currentTotal = current.price + (current.shipping || 0)
      const bestTotal = best.price + (best.shipping || 0)
      return currentTotal < bestTotal ? current : best
    })
  }
  
  static getPriceTrendIcon(trend: 'rising' | 'falling' | 'stable'): string {
    switch (trend) {
      case 'rising': return '📈'
      case 'falling': return '📉'
      case 'stable': return '➡️'
    }
  }
  
  static formatPriceChange(changePercentage: number): { text: string, color: string } {
    const isPositive = changePercentage > 0
    const absChange = Math.abs(changePercentage)
    
    return {
      text: `${isPositive ? '+' : ''}${changePercentage}%`,
      color: isPositive ? 'text-red-600' : 'text-green-600'
    }
  }
}